<template>
  <div
    class="searchbar"
    :class="[context, { disappearing, focused, tour, recent: searchHistory.length > 0 }]"
  >
    <div class="search-row">
      <input
        ref="searchinput"
        v-model="query"
        autocomplete="off"
        type="text"
        placeholder="Search"
        aria-label="Search"
        @keyup.enter="searchInput"
        @focus="focus"
        @blur="blur"
        @keydown.down="nextSearch('down')"
        @keydown.up="nextSearch('up')"
        @keydown.esc="cancelRecentSearch"
      >
      <button
        class="clear"
        aria-label="Clear search input"
        @click="clear"
      >
        <icon
          name="cross"
          variant="666666"
        />
      </button>
      <button
        class="search"
        aria-label="Search"
        @click="searchInput"
      >
        <icon
          class="search-icon"
          name="search"
          variant="white"
        />
        <bookmark-tourtip
          v-if="showBookmarkTourtip(1)"
          :sequence="1"
        />
      </button>
    </div>
    <ul
      v-if="searchHistory.length > 0"
      ref="search-history"
      class="search-history"
      :class="{ reset }"
    >
      <li>
        RECENT SEARCHES
        <button
          class="clear-history"
          @click="clearHistory"
        >
          Clear history
        </button>
      </li>
      <li
        v-for="(term, index) in searchHistory"
        :key="index"
        ref="recentSearch"
        :class="{activeRecentSearch: activeRecentSearchIndex === index}"
      >
        <button
          class="search-term"
          @click="searchResult(term)"
        >
          {{ term }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import BookmarkTourtip from '@components/tour/bookmarks/bookmarkTourtip';
import Icon from '@components/singletons/icon';

import GlobalMixin from '@/mixins/global';

import { getClassTourConfig, incrementTourSequence } from '@/utils/tour';
import { setChecklistData, TOPIC_SEARCH_KEY } from '@/utils/checklist';

export default {
  components: { BookmarkTourtip, Icon },
  mixins: [GlobalMixin],
  props: {
    context: {
      type: String,
    },
  },
  emits: ['search'],
  data() {
    return {
      focused: false,
      reset: false, // used to make recent search list disappear even when user is hovering
      query: '',
      activeRecentSearchIndex: null,
    };
  },
  computed: {
    disappearing() {
      return this.$route.meta.disappearingSearchbar;
    },
    searchHistory() {
      return this.$store.getters['search/history'];
    },
    tour() {
      return this.$store.getters['session/onTour'];
    },
  },
  watch: {
    '$route'(to, from) {
      let toSearch = to.query.q;
      let fromSearch = from.query.q;
      if(fromSearch != toSearch) {
        this.query = toSearch;
        this.$emit('search', toSearch);
      }
    },
    tour(starting) {
      if (starting && this.context === 'widget') {
        const config = getClassTourConfig(this.$store);
        if (config && config.query) {
          this.query = config.query;
        }
      }
    },
  },
  created() {
    let q = this.$route.query.q;
    if (q) {
      this.query = q;
    }
  },
  methods: {
    blur(event) {
      this.$store.commit('appState/setInputtingText', false);
      // There are some issues when selecting something from recent searches when the search bar is in the topbar
      // It's caused when the `blur` event is fired too soon and the click event is fired on the app cointainer instead of the recent search term
      // This only happens in the topbar & widget, though. That's why we check for the context here. If we don't it causes issues with the searchbar on the search page.
      // This will check if there is an event passed in and if there is, if it has a related target. In the instance of the `blur` event,
      // the relatedTarget will be the element that received the click/focus event ie, the thing you clicked on. We can use to this to check if
      // the thing clicked is in fact a recent search item and if it is, stop the blur event from happening. The search will then complete and
      // this function will get called without the event parameter allowing the recent searches to close.
      let searchTermClicked;
      if (event) {
        // FF and Chrome should hit this but IE returns null for the relatedTarget
        // We can get around this a little bit by looking at the activeElement to determine
        // if it was a search term that was clicked.
        // Safari won't work at all... https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Clicking_and_focus
        searchTermClicked = event.relatedTarget?.classList?.contains('search-term');
        if (!searchTermClicked) {
          searchTermClicked = document.activeElement.classList?.contains('search-term');
        }
      }

      if (['topbar', 'widget'].includes(this.context) && searchTermClicked) {
        return;
      }
      this.focused = false;
    },
    cancelRecentSearch() {
      this.activeRecentSearchIndex = null;
    },
    clear() {
      this.query = '';
      if (this.$refs.searchinput) this.$refs.searchinput.focus();
      if (this.context === 'results') {
        this.search('');
      }
    },
    clearHistory() {
      this.$store.commit('search/clearSearch');
    },
    focus() {
      if (this.$store.getters['session/onTour']) return;
      this.$store.commit('appState/setInputtingText', true);
      // The context on the search view is 'results' and so anywhere other than that,
      // we want to send this event
      const onSearchView = this.context === 'results';
      if (!onSearchView) {
        this.$analytics.events.openedSearch(this.currentResource, this.$route, this.$store);
      }
      this.focused = true;
      this.reset = false;
    },
    nextSearch(direction) {
      // This method will handle moving through the recent searches with the arrow keys
      // The index will be set to null and reset the counter variable
      // when the arrows reach the top or bottom of the list
      if (direction === 'up') {
        if (this.activeRecentSearchIndex === null) {
          this.activeRecentSearchIndex = this.searchHistory.length - 1;
        } else if (this.activeRecentSearchIndex === 0) {
          this.activeRecentSearchIndex = null;
        } else {
          this.activeRecentSearchIndex -= 1;
        }
      } else if (direction === 'down') {
        if (this.activeRecentSearchIndex === null) {
          this.activeRecentSearchIndex = 0;
        } else if (this.activeRecentSearchIndex + 1 === this.searchHistory.length) {
          this.activeRecentSearchIndex = null;
        } else {
          this.activeRecentSearchIndex += 1;
        }
      }
    },
    search(query) {
      setChecklistData(this.$store, TOPIC_SEARCH_KEY, this.$notify);
      this.$router.push({ name: 'search#index', query: { q: query }});
    },
    searchInput() {
      const query = this.activeRecentSearchIndex !== null ? this.searchHistory[this.activeRecentSearchIndex] : this.query;
      if (query) {
        this.$store.commit('search/recordSearch', query);
        this.search(query);
        if (this.context !== 'widget') {
          this.$refs.searchinput.blur();
          this.activeRecentSearchIndex = null;
        }
        if (this.tour) {
          incrementTourSequence.apply(this, [2]);
        }
      }
    },
    searchResult(query) {
      this.search(query);
      if (this.context === 'results') {
        this.query = query;
        this.reset = true;
      }
    },
    showBookmarkTourtip(sequence) {
      return this.tour && sequence === this.$store.getters['session/tourSequence'];
    },
  },
};

</script>

<style lang='scss' scoped>
.searchbar {
  background: white;
  border: solid 1px #dadada;

  .search-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: rem-calc(250);
  }

  input {
    box-sizing: border-box;
    color: #666666;
    font-weight: $semi-bold;
    height: rem-calc(38);
    margin: rem-calc(1) 0 rem-calc(1) rem-calc(10);
    padding: rem-calc(6);
    background: transparent;

    &::-ms-clear {
      display: none;
    }
  }

  button {
    &.clear {
      background-color: inherit;
      display: none;
      height: rem-calc(30);
      padding: rem-calc(2);
      width: rem-calc(30);

      &:active {
        display: block;
      }
    }

    &.search {
      background-color: #00d8f8;
      border-radius: rem-calc(15);
      height: rem-calc(26);
      width: rem-calc(26);
      padding: 0;

      .search-icon {
        width: rem-calc(12);
        height: auto;
        margin-top: rem-calc(2);
      }
    }
  }

  ul.search-history {
    background: white;
    border: solid 1px #dadada;
    border-top: none;
    border-radius: 0 0 rem-calc(15) rem-calc(15);
    display: none;
    list-style-type: none;
    margin: 0 0 rem-calc(18) 0;
    transform: translateX(-1px);
    width: rem-calc(251);
    box-sizing: border-box;
    position: absolute;
    top: rem-calc(38);
    display: none;
    padding: 0;

    li {
      color: black;
      display: flex;
      font-size: rem-calc(12);
      letter-spacing: 0.5px;
      margin: rem-calc(12) 0;
      padding: 0 rem-calc(16);

      &:not(:first-child) {
        cursor: pointer;

        &:hover,
        &.activeRecentSearch {
          text-decoration: underline;
        }
      }

      &:first-child {
        font-weight: $bold;
      }

      .clear-history {
        background: white;
        color: #666666;
        font-size: rem-calc(11);
        margin-left: auto;
        padding-right: 0;
        text-decoration: underline;
      }

      .search-term {
        background: transparent;
        padding: 0;
      }
    }

    &:not(.reset):hover {
      display: block;
    }
  }

  &:not(.topbar) {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
    left: 0;
    margin: auto;
    right: 0;
  }

  &.topbar {
    border-radius: rem-calc(15);
    max-width: rem-calc(400);
    background-color: #edf1f5;
    border-color: transparent;

    input {
      color: #000000;
      font-weight: $normal;
      height: rem-calc(28);
      margin-left: rem-calc(12);
      padding: rem-calc(4);
      max-width: calc(100% - 62px);

      @include breakpoint(small) {
        width: calc(100% - 104px);
      }
    }

    button {
      &.clear {
        height: rem-calc(22);
        padding: rem-calc(3);
        width: rem-calc(22);
      }

      &.search {
        height: rem-calc(22);
        width: rem-calc(22);
        margin-right: rem-calc(6);

        .search-icon {
          width: rem-calc(10);
          height: auto;
          margin-bottom: 1px;
        }

        @include breakpoint(small) {
          height: rem-calc(28);
          padding: rem-calc(7);
          width: rem-calc(28);
        }
      }
    }
    &.focused {
      background-color: white;
      border: solid 1px #dadada;
    }
  }

  &.focused.recent {
    border-radius: rem-calc(15) rem-calc(15) 0 0;
    z-index: 15;

    button.clear {
      display: block;
    }

    ul {
      box-sizing: border-box;
      display: block;
    }
  }

  &.tour {

    input {
      pointer-events: none;
    }

    &.widget {
      box-shadow: none;

      button {
        &.search {
          animation: blue-pulse 1.5s infinite;
        }
      }
    }
  }

  &.results {
    // if user searches from home we want searchbars to be about the same
    margin-top: rem-calc(20);
  }

  &.topbar.disappearing {
    display: none;

    @include breakpoint(small) {
      display: initial;
    }
  }
}
</style>
